import styled from "styled-components";
import { colors } from "../../../styles/figmaColors";
import { typographies } from "../../../styles/figmaTypographies";
import { Spacer } from "./../Spacer";
import { TextCapitalized } from "../text/TextCapitalized";
import { SearchBarDS, SearchBarDSProps } from "../SearchBar";
import { t } from "i18next";
import { useId } from "react";
import { TooltipHover } from "../TooltipHover";

export interface MenuOptionsProps<
  Key extends string,
  T extends Record<Key, string | number | null | boolean>,
> {
  options: Option[];
  hasBorderRow?: boolean;
  hasHoverBackground?: boolean;
  bottomButton?: JSX.Element;
  width?: string;
  searchBar?: SearchBarDSProps<T>;
  searchBarPlaceholder?: string;
}

export interface Option {
  label: string | JSX.Element;
  action: (id?: string) => void;
  color?: string;
  icon?: JSX.Element;
  isDisabled?: (id?: string) => boolean;
  disabledHoverTooltip?: (id?: string) => string | null;
}

export const MenuOptions = <
  Key extends string,
  T extends Record<Key, string | number | null>,
>(
  props: MenuOptionsProps<Key, T>,
) => {
  const tooltipId = useId();
  const setSearchValues = (values: T[]) => {
    props.searchBar?.setFilterValues(values);
  };

  return (
    <StyledContainer $width={props.width}>
      {props.hasBorderRow ? <Spacer y={0.25} /> : <Spacer y={0.5} />}

      <StyledOverflow>
        {props.options.map((option, index) => {
          const isLastRow = index === props.options.length - 1;
          const isFirstRow = index === 0;
          const hasBorderRow = props.hasBorderRow ?? false;
          const isDisabled = option.isDisabled?.() ?? false;
          const disabledHoverTooltip = isDisabled
            ? (option.disabledHoverTooltip?.() ?? null)
            : null;
          return (
            <div key={"row" + index}>
              <StyledRowOption
                $hasBorderRow={hasBorderRow}
                $isLastRow={isLastRow}
              >
                <TooltipHover
                  id={tooltipId}
                  place="right"
                  content={disabledHoverTooltip ?? ""}
                />
                {!isFirstRow && hasBorderRow && <Spacer y={0.25} />}
                <StyledFlex>
                  <Spacer x={0.5} />
                  <StyledOption
                    onClick={() => {
                      if (isDisabled) return;
                      option.action();
                    }}
                    $isDisabled={isDisabled}
                    $color={option.color}
                    $hasHoverBackground={props.hasHoverBackground ?? false}
                    data-tooltip-id={
                      disabledHoverTooltip ? tooltipId : undefined
                    }
                  >
                    <Spacer y={0.75} />
                    <StyledRow>
                      {option.icon}
                      {option.icon && <Spacer x={0.25} />}
                      <Spacer x={0.5} />
                      <StyledLabel>
                        <Spacer x={1.5} />
                        <TextCapitalized>{option.label}</TextCapitalized>
                      </StyledLabel>
                    </StyledRow>
                    <Spacer y={0.75} />
                  </StyledOption>
                  <Spacer x={0.5} />
                </StyledFlex>
                {hasBorderRow && <Spacer y={0.25} />}
              </StyledRowOption>
            </div>
          );
        })}
      </StyledOverflow>
      {props.searchBar && (
        <>
          <Spacer y={0.5} />
          <StyledLine />
          <Spacer y={1.5} />
          <StyledRow>
            <Spacer x={1} />
            <SearchBarDS
              setFilterValues={setSearchValues}
              placeholder={
                props.searchBarPlaceholder ?? t("menuOptions.searchbar")
              }
              values={props.searchBar.values}
              keysToIgnore={props.searchBar.keysToIgnore}
            />
            <Spacer x={1.0625} />
          </StyledRow>
          <Spacer y={1.5} />
        </>
      )}
      {props.bottomButton && (
        <>
          <StyledRow>
            <Spacer x={1} />
            {props.bottomButton}
            <Spacer x={1.0625} />
          </StyledRow>
          <Spacer y={0.5} />
        </>
      )}
    </StyledContainer>
  );
};

interface StyledContainerArgs {
  $color?: string;
  $width?: string;
  $isDisabled?: boolean;
  $hasHoverBackground?: boolean;
}

const StyledOverflow = styled.div`
  display: flex;
  overflow: auto;
  flex-direction: column;
  max-height: 27rem;
`;

const StyledLine = styled.div`
  display: flex;
  border-top: 0.0625rem solid ${colors["colors/borders/cells/cells"]};
`;

const StyledContainer = styled.div<StyledContainerArgs>`
  background-color: ${colors["colors/surfaces/background/background_level0"]};
  border: 0.0625rem solid ${colors["colors/borders/cards/cards"]};
  border-radius: 0.5rem;
  width: ${({ $width }) => ($width ? $width : "14.1875")}rem;
  box-shadow: 0.125rem 0.0625rem 0.25rem 0rem rgba(122, 122, 122, 0.08);
`;

const StyledRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const StyledRowOption = styled.div<{
  $hasBorderRow: boolean;
  $isLastRow: boolean;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: ${({ $hasBorderRow, $isLastRow }) =>
    $hasBorderRow && !$isLastRow
      ? `0.0625rem solid ${colors["colors/borders/cells/cells"]}`
      : "none"};
`;

const StyledLabel = styled.div`
  width: 100%;
`;

const StyledFlex = styled.div`
  display: flex;
  width: 100%;
`;

const StyledOption = styled.div<StyledContainerArgs>`
  width: 100%;
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  ${typographies["Body/M"]};
  color: ${({ $color, $isDisabled }) =>
    $isDisabled
      ? colors["colors/text/lightGrey"]
      : $color
        ? $color
        : colors["colors/text/darkGrey"]};

  border-radius: 0.5rem;

  &:hover {
    cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "pointer")};
    ${({ $hasHoverBackground }) =>
      $hasHoverBackground &&
      `background-color: ${colors["colors/surfaces/background/background_level1"]};`}
  }
  & svg {
    width: 1rem;
    height: 1rem;
    fill: ${({ $color, $isDisabled }) =>
      $isDisabled
        ? colors["colors/text/lightGrey"]
        : $color
          ? $color
          : colors["colors/text/darkGrey"]};
  }
`;
